/**
 * Company Profile API Client
 */

import { apiClient } from './client';
import type { components } from './schema.gen';
import type {
  CompanyProfile,
  CompanyProfileCreate,
  CompanyProfileUpdate,
  CompanyProfileSummary,
  DataSourceStatus,
  MetricsSummary,
  IntegrationConnect,
  SyncStatus,
  ProfileCompletenessCheck,
} from '@/types';

export type ProfileAnalysisResult = components['schemas']['ProfileAnalysisResult'];

// === CRUD Operations ===

/**
 * Create a new company profile
 */
export async function createCompanyProfile(data: CompanyProfileCreate): Promise<CompanyProfile> {
  const response = await apiClient.post<CompanyProfile>('/company-profile/', data);
  return response.data;
}

/**
 * Onboarding analyzer — fetch a domain's site and get a proposed profile.
 * Used by the wizard's "Analyze site" button to pre-fill fields precisely.
 */
export async function analyzeCompanySite(domain: string): Promise<ProfileAnalysisResult> {
  const response = await apiClient.post<ProfileAnalysisResult>('/company-profile/analyze', {
    domain,
  });
  return response.data;
}

/**
 * Re-run the onboarding analyzer for an existing profile — refreshes its seo_brief
 * (and fills empty strategic fields), returning the updated profile.
 */
export async function reanalyzeProfile(profileId: string): Promise<CompanyProfile> {
  const response = await apiClient.post<CompanyProfile>(
    `/company-profile/profiles/${profileId}/reanalyze`
  );
  return response.data;
}

// === Multi-Profile Operations ===

/**
 * Get all profiles for the current user
 */
export async function getAllProfiles(): Promise<CompanyProfile[]> {
  const response = await apiClient.get<CompanyProfile[]>('/company-profile/profiles');
  return response.data;
}

/**
 * Get a specific profile by ID
 */
export async function getProfileById(profileId: string): Promise<CompanyProfile> {
  const response = await apiClient.get<CompanyProfile>(`/company-profile/profiles/${profileId}`);
  return response.data;
}

/**
 * Activate a specific profile
 */
export async function activateProfile(profileId: string): Promise<CompanyProfile> {
  const response = await apiClient.post<CompanyProfile>(
    `/company-profile/profiles/${profileId}/activate`
  );
  return response.data;
}

/**
 * Get current user's company profile
 */
export async function getMyCompanyProfile(): Promise<CompanyProfile> {
  const response = await apiClient.get<CompanyProfile>('/company-profile/');
  return response.data;
}

/**
 * Get company profile summary (lightweight)
 */
export async function getCompanyProfileSummary(): Promise<CompanyProfileSummary> {
  const response = await apiClient.get<CompanyProfileSummary>('/company-profile/summary');
  return response.data;
}

/**
 * Update company profile
 */
export async function updateCompanyProfile(
  profileId: string,
  data: CompanyProfileUpdate
): Promise<CompanyProfile> {
  const response = await apiClient.put<CompanyProfile>(`/company-profile/${profileId}`, data);
  return response.data;
}

/**
 * Delete company profile
 */
export async function deleteCompanyProfile(profileId: string): Promise<void> {
  await apiClient.delete(`/company-profile/${profileId}`);
}

// === Data Source Management ===

/**
 * Get status of all data sources
 */
export async function getDataSourcesStatus(): Promise<DataSourceStatus> {
  const response = await apiClient.get<DataSourceStatus>('/company-profile/data-sources/status');
  return response.data;
}

/**
 * Connect a data source to the profile
 * (After OAuth is completed)
 */
export async function connectDataSource(
  data: IntegrationConnect
): Promise<{ message: string; profile: CompanyProfile }> {
  const response = await apiClient.post<{ message: string; profile: CompanyProfile }>(
    '/company-profile/data-sources/connect',
    data
  );
  return response.data;
}

/**
 * Disconnect a data source
 */
export async function disconnectDataSource(
  integrationType: 'gsc' | 'ga4' | 'google_ads'
): Promise<{ message: string; profile: CompanyProfile }> {
  const response = await apiClient.post<{ message: string; profile: CompanyProfile }>(
    `/company-profile/data-sources/disconnect/${integrationType}`
  );
  return response.data;
}

// === Metrics & Sync ===

/**
 * Get metrics summary
 */
export async function getMetricsSummary(): Promise<MetricsSummary> {
  const response = await apiClient.get<MetricsSummary>('/company-profile/metrics');
  return response.data;
}

/**
 * Trigger manual sync for a data source
 */
export async function triggerManualSync(dataSource: 'gsc' | 'ga4' | 'google_ads'): Promise<SyncStatus> {
  const response = await apiClient.post<SyncStatus>(`/company-profile/sync/${dataSource}`);
  return response.data;
}

/**
 * Check profile completeness
 */
export async function checkProfileCompleteness(): Promise<ProfileCompletenessCheck> {
  const response = await apiClient.get<ProfileCompletenessCheck>('/company-profile/completeness');
  return response.data;
}
